import type { AddToSalaryMapping, BulkUpdateEmploymentsStatus, Employment, EmploymentCreate, EmploymentUpdate } from '@sysarb/sysarb-types/services/v1/employment';
import type { Guid } from 'guid-typescript';
import type { useResgate, Collection } from '@/login/composables/useResgate';
import { useLoginStore } from '@/login/stores/useLoginStore';

export class EmploymentService {
    public async getAll(resgate: ReturnType<typeof useResgate>): Promise<Iterable<Employment> | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.collection<Employment>(`v1.${instanceTopic}.employments`).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async getAllByOrganizationNode(resgate: ReturnType<typeof useResgate>, id: string): Promise<Collection<Employment>[]> {
        let fetchMore = true;
        const result: Collection<Employment>[] = [];
        const { instanceTopic } = useLoginStore();

        let page = 0;
        const limit = 100;

        while (fetchMore) {

            const response = await resgate.collection<Employment>(`v1.${instanceTopic}.employments.bynode.${id}?page=${page}&limit=${limit}`).then(res => {
                if (res?.success === false) {
                    return null;
                }
                return res.data;
            });

            if (response != null) {
                result.push(response);
            }

            if (response == null || response.length < limit) {
                fetchMore = false;
                break;
            }

            page++;
        }

        return result;
    }

    public async getByEmployeeId(resgate: ReturnType<typeof useResgate>, employeeId: Guid | string): Promise<Collection<Employment> | null> {
        if (employeeId == null || employeeId === '') return Promise.resolve(null);
        const { instanceTopic } = useLoginStore();
        return resgate.collection<Employment>(`v1.${instanceTopic}.employments.byemployee.${employeeId}`).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    //TODO change from unkown to exported type from service when types in the service are fixed
    public async update(resgate: ReturnType<typeof useResgate>, employmentId: Guid | string, updateData: EmploymentUpdate): Promise<unknown> {
        const { instanceTopic } = useLoginStore();
        return resgate.call(`v1.${instanceTopic}.employment.${employmentId}`, 'update', updateData);
    }

    public async create(resgate: ReturnType<typeof useResgate>, createData: EmploymentCreate): Promise<unknown> {
        const { instanceTopic } = useLoginStore();
        return resgate.call(`v1.${instanceTopic}.employments`, 'create', createData);
    }

    public async deleteMultiple(resgate: ReturnType<typeof useResgate>, employmentIds: string[]): Promise<unknown> {
        const { instanceTopic } = useLoginStore();
        return resgate.call(`v1.${instanceTopic}.employments`, 'delete', { ids: employmentIds });
    }

    public async updatestatus(resgate: ReturnType<typeof useResgate>, employmentIds: string[], newStatus: number): Promise<{ syncJobId: string } | null> {
        const updateData: BulkUpdateEmploymentsStatus = {
            ids: employmentIds,
            newValue: newStatus
        };
        const { instanceTopic } = useLoginStore();
        return resgate.call<{ syncJobId: string }>(`v1.${instanceTopic}.employments`, 'updatestatus', updateData).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async addToSalaryMapping(resgate: ReturnType<typeof useResgate>, employmentIds: string[], mappingId: string, skipNewGroups: boolean): Promise<null> {
        const data: AddToSalaryMapping = {
            ids: employmentIds,
            mapping: mappingId,
            skipNewGroups
        };
        const { instanceTopic } = useLoginStore();

        return resgate.call<null>(`v1.${instanceTopic}.employments`, 'addtomapping', data).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }

    public async createHistoricalEmployment(resgate: ReturnType<typeof useResgate>, employmentId: Guid | string): Promise<unknown> {
        const { instanceTopic } = useLoginStore();
        return resgate.call(`v1.${instanceTopic}.employment.${employmentId}`, 'createhistoricalemployment', {});
    }

    public async getHistoricalEmployments(resgate: ReturnType<typeof useResgate>, employmentId: Guid | string): Promise<Collection<Employment> | null> {
        const { instanceTopic } = useLoginStore();
        return resgate.collection<Employment>(`v1.${instanceTopic}.employment.${employmentId}.historicalemployments`).then(res => {
            if (res?.success === false) {
                return null;
            }
            return res.data;
        });
    }
}
